// ORDER-TYPE-EXCEPTION-CRUD
import { useMemo, useState } from "react";
import * as yup from "yup";
import { Button, Modal } from "src/components";
import SmartForm from "src/container/smart-form.component";
import { useToast } from "src/hooks/useToast";
import { editOrderTypeException } from "src/services/order-api";
import { editableOrderTypes } from "./order-type-exception.constants";

interface EditOrderTypeExceptionProps {
  record: any;
  onClose: () => void;
  onSuccess: () => void;
}

const schema = yup.object().shape({
  order_type: yup.object().nullable().required("Order Type is required"),
  reference_number: yup.string().trim().nullable().max(150, "Remarks must not exceed 150 characters"),
});

// Only the order type is editable; the rest of the record identifies it and stays fixed.
const EditOrderTypeException = ({ record, onClose, onSuccess }: EditOrderTypeExceptionProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  // SmartForm resets whenever defaultValues changes identity, so it must stay stable across renders
  const defaultValues = useMemo(
    () => ({ order_type: editableOrderTypes.find((type) => type.code === record?.cndn_remarks_code_int) ?? null }),
    [record]
  );
  const metadata = record?.novu_metadata;

  const details = [
    { label: "Company", value: metadata?.company?.companyName },
    { label: "Division", value: metadata?.divisions?.[0]?.divisionName },
    { label: "Counter", value: record?.counter_code && `${record.counter_code} - ${record.location_code}` },
    { label: "Stockist", value: record?.stockist_code !== "null" && record?.stockist_code },
    { label: "Item", value: record?.item_code },
    { label: "State", value: metadata?.states?.[0]?.stateName },
    { label: "Module", value: record?.module_desc },
  ];

  const handleSubmit = async (values: any) => {
    if (values.order_type?.code === record?.cndn_remarks_code_int) {
      toast({ description: "Select a different order type.", variant: "destructive" });
      return;
    }

    try {
      setLoading(true);
      await editOrderTypeException(
        {
          order_type_exceptionid: record.order_type_exceptionid,
          order_type: values.order_type.value,
          reference_number: values.reference_number?.trim() || null,
        },
        record.company_code
      );
      toast({ description: "Order type updated successfully." });
      onClose();
      onSuccess();
    } catch (err: any) {
      console.error("Error editing order type exception:", err);
      toast({
        description: err?.response?.data?.message || err?.message || "Failed to update order type.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Edit Order Type" isHeader={true} isOpen={!!record} onCloseModal={onClose} onBack={onClose}>
      <div className="p-5 w-[480px]">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 mb-5 text-sm">
          {details.map((detail) => (
            <div key={detail.label}>
              <dt className="text-xs text-gray-500">{detail.label}</dt>
              <dd className="font-medium text-gray-800">{detail.value || "-"}</dd>
            </div>
          ))}
        </dl>
        <SmartForm defaultValues={defaultValues} isFormChangedCheck={false} schema={schema} onFormSubmit={handleSubmit}>
          <div className="flex flex-col gap-4">
            <SmartForm.Autocomplete
              className=""
              placeholder="Select Order Type"
              fieldName="order_type"
              label="Order Type"
              suggestionList={editableOrderTypes}
              uniqueKey="value"
              displayKey="label"
            />
            <SmartForm.Input type="text" className="" placeholder="Remarks" fieldName="reference_number" label="Remarks" />
          </div>
          <footer className="flex items-center justify-between gap-3 mt-6">
            <Button onClick={onClose} className="border bg-primary text-white flex-1 py-[0.62rem]">
              Cancel
            </Button>
            <Button isLoading={loading} loaderSize="small" type="submit" className="border flex-1 py-[0.62rem]">
              Update
            </Button>
          </footer>
        </SmartForm>
      </div>
    </Modal>
  );
};

export default EditOrderTypeException;
// ORDER-TYPE-EXCEPTION-CRUD
